import React, { useState, useEffect, useRef } from 'react'
import {
  FormScrollWrapper,
  ScrollableWrapper,
  FullScreenCard,
  FormContainer,
  Row,
  ButtonContainer,
  CheckboxGroup,
  CheckboxLabel,
  StepIndicator,
  TitleContainer,
  PageContainer,
  StepCard,
  InfoBox,
  WarningBox,
  Box,
  Divider,
  MobileInputWrapper,
} from './styles'
import Grid from '@mui/material/Grid'
import Typography from '@/v2/components/common/Typography'
import { useTheme } from '@mui/material/styles'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import Button from '@/v2/components/common/Button'
import TextField from '@/v2/components/common/TextField'
import SelectCalendar from '@/v2/components/common/SelectCalendar'
import DateOfBirthInput from '@/v2/components/common/DateOfBirthInput'
import {
  verificationSchema,
  processedInsuranceSchema,
  insuranceSchema,
} from './rules'
import {
  useAuthenticateInsurance,
  useCreateInsurance,
  useUpdateInsurance,
  useUpdateInsurancePrivate,
} from '@/v2/hooks/insurance/useInsuranceForm'
import InsuranceCardUploads from './InsuranceCardUploads'
import PayerSearch from '@/v2/components/common/PayerSearch'
import SelectField from '@/v2/components/common/SelectField'
import { useQueryClient } from '@tanstack/react-query'
import {
  removeNullProperties,
  formatDateForSubmit,
  buildInitialFormValues,
  extractInsuranceFields,
} from './utils'

const InsuranceForm = () => {
  const theme = useTheme()
  const { patientHashId } = useParams()
  const { authenticateInsurance } = useAuthenticateInsurance(patientHashId)
  const { createInsurance } = useCreateInsurance(patientHashId)
  const { updateInsurance } = useUpdateInsurance(patientHashId)
  const navigate = useNavigate()
  const location = useLocation()
  const { updateInsurancePrivate } = useUpdateInsurancePrivate(
    location?.state?.patient?.id,
    location?.state?.insuranceForm?.id
  )
  const [step, setStep] = useState(1)
  const [method, setMethod] = useState('post')
  const [formValues, setFormValues] = useState({})
  const [errors, setErrors] = useState({})
  const [isSigned, setIsSigned] = useState(false)
  const [multipleInsurances, setMultipleInsurances] = useState([])
  const [selectedInsuranceIndex, setSelectedInsuranceIndex] = useState(null)
  const queryClient = useQueryClient()
  const [statusChanged, setStatusChanged] = useState(false)
  const previousStatus = useRef(null)
  const [isMobile, setIsMobile] = useState(false)
  const formRef = useRef(null)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    // Set viewport meta tag to prevent zoom on input focus
    let viewport = document.querySelector('meta[name=viewport]')
    if (!viewport) {
      viewport = document.createElement('meta')
      viewport.name = 'viewport'
      document.head.appendChild(viewport)
    }
    viewport.content =
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'

    const handleFocus = e => {
      if (isMobile && e.target.tagName === 'INPUT') {
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 300)
      }
    }

    document.addEventListener('focusin', handleFocus)

    return () => {
      window.removeEventListener('resize', checkMobile)
      document.removeEventListener('focusin', handleFocus)
      // Reset viewport on unmount
      if (viewport) {
        viewport.content = 'width=device-width, initial-scale=1.0'
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (location?.state?.patient) {
      setIsSigned(true)
      const patient = location.state.patient
      const insuranceForm = location.state?.insuranceForm

      setMethod(insuranceForm ? 'put' : 'post')
      setStep(3)
      setFormValues(buildInitialFormValues({ patient, insuranceForm }))
      previousStatus.current = insuranceForm?.status
    }
  }, [location])

  const handleNext = async () => {
    if (step === 1) {
      try {
        await verificationSchema.validate(
          {
            verificationEmail: formValues.verificationEmail,
          },
          { abortEarly: false }
        )
        setErrors({})
        const res = await authenticateInsurance({
          patient_auth: {
            email: formValues.verificationEmail,
          },
        })
        if (res.error) {
          setErrors({ verificationEmail: 'Invalid Email' })
          return
        }
        const insArray = res.insuranceForms || []
        // The authenticate endpoint returns the patient (so we can pre-fill the
        // name, gender and DOB without asking) plus their active insurance forms.
        // Selecting an existing form in step 2 later overrides these via
        // extractInsuranceFields.
        const patientPrefill = {
          firstName: res.patient?.firstName || '',
          lastName: res.patient?.lastName || '',
          gender: res.patient?.gender || '',
          dateOfBirth: res.patient?.dob || '',
          email: formValues.verificationEmail,
        }
        if (insArray.length >= 1) {
          setMultipleInsurances(insArray)
          setStep(2)
          setFormValues(prev => ({ ...prev, ...patientPrefill }))
        } else {
          setMethod('post')
          setFormValues(prev => ({ ...prev, ...patientPrefill }))
          setStep(3)
        }
      } catch (err) {
        const validationErrors = {}
        err?.inner?.forEach(error => {
          validationErrors[error.path] = error.message
        })
        setErrors(validationErrors)
      }
    } else if (step === 2) {
      handleInsuranceSelectionNext()
    }
  }

  const handleInsuranceSelectionNext = () => {
    if (selectedInsuranceIndex === null) {
      setErrors({ form: 'Please select an insurance or create a new one.' })
      return
    }
    if (selectedInsuranceIndex === -1) {
      setMethod('post')
      setFormValues(prev => ({ ...prev, status: 'draft' }))
    } else {
      const chosen = multipleInsurances[selectedInsuranceIndex]
      const fields = extractInsuranceFields(chosen)
      setMethod('put')
      setFormValues(prev => ({
        id: chosen.id,
        ...prev,
        ...fields,
      }))
    }
    setErrors({})
    setStep(3)
  }

  const handleSubmit = async () => {
    try {
      setErrors({})
      if (formValues.status === 'draft' || !formValues.status)
        await insuranceSchema.validate(formValues, { abortEarly: false })
      else
        await processedInsuranceSchema.validate(formValues, {
          abortEarly: false,
        })

      const payload = {
        patient_auth: {
          email: formValues.verificationEmail || formValues.email || '',
        },
        insurance_form: {
          status: formValues.status || 'draft',
          id: formValues.id || null,
          insurance_form_patient_attributes: {
            id: formValues.patientId || null,
            first_name: formValues.firstName,
            last_name: formValues.lastName,
            gender: formValues.gender,
            dob: formatDateForSubmit(formValues.dateOfBirth),
            employer_name: formValues.employerName,
            insurance_company: formValues.insuranceCompany,
            insurance_coverage_start: formValues.insuranceCoverageStartDate,
            plan_group_number: formValues.planNumber,
            insurance_payer_id: formValues.payerId,
            member_id: formValues.memberId,
            insurance_card_front: formValues.insurance_card_front?.includes(
              'http'
            )
              ? null
              : formValues.insurance_card_front,
            insurance_card_back: formValues.insurance_card_back?.includes(
              'http'
            )
              ? null
              : formValues.insurance_card_back,
            staff_consult_policy_holder_subscriber_id:
              formValues.staff_consult_policy_holder_subscriber_id,
          },
          insurance_form_subscriber_attributes: {
            id: formValues.subscriberId || null,
            first_name: formValues.policyHolderFirstName,
            last_name: formValues.policyHolderLastName,
            gender: formValues.policyHolderGender,
            dob: formatDateForSubmit(formValues.policyHolderDateOfBirth),
            employer_name: formValues.policyHolderEmployerName,
            relation_to_patient: formValues.relationToPatient,
            member_id: formValues.policyHolderMemberId,
          },
        },
      }

      const cleanedPayload = removeNullProperties(payload)

      // Gender must always be sent even when null/empty,
      // otherwise the backend keeps stale values on update
      cleanedPayload.insurance_form.insurance_form_patient_attributes.gender =
        formValues.gender || null

      const hasAnySubscriberData = [
        formValues.policyHolderFirstName,
        formValues.policyHolderLastName,
        formValues.policyHolderGender,
        formValues.policyHolderDateOfBirth,
        formValues.policyHolderEmployerName,
        formValues.relationToPatient,
        formValues.policyHolderMemberId,
      ].some(value => typeof value === 'string' && value.trim() !== '')

      if (hasAnySubscriberData) {
        cleanedPayload.insurance_form.insurance_form_subscriber_attributes.gender =
          formValues.policyHolderGender || null
      } else {
        // Empty hash tells the backend to clear the subscriber entirely
        cleanedPayload.insurance_form.insurance_form_subscriber_attributes = {}
      }

      let response
      if (isSigned) {
        if (method === 'put') {
          response = await updateInsurancePrivate(cleanedPayload)
        } else {
          response = await createInsurance(cleanedPayload)
        }
      } else {
        response =
          method === 'post'
            ? await createInsurance(cleanedPayload)
            : await updateInsurance(cleanedPayload)
      }
      if (response.error) {
        setErrors({ form: 'Submission failed. Please check your details.' })
        return
      }
      setStep(4)
      queryClient.setQueryData(['PatientProfile', formValues.id], oldData => {
        if (!oldData) return oldData
        return {
          ...oldData,
          insuranceForm: response,
        }
      })
    } catch (err) {
      console.error('Submission error:', err)
      const validationErrors = {}
      err?.inner?.forEach(error => {
        validationErrors[error.path] = error.message
      })
      setErrors(validationErrors)
    }
  }

  const renderVerificationStep = () => (
    <FormContainer>
      <Typography variant="body2" gutterBottom>
        Please verify your identity to continue
      </Typography>
      <Row>
        <TextField
          label="Email *"
          name="verificationEmail"
          placeholder="example@example.com"
          fullWidth
          value={formValues.verificationEmail || ''}
          onChange={e => setFormValues({ ...formValues, verificationEmail: e })}
          error={errors?.verificationEmail}
        />
      </Row>
      {errors.form && (
        <Typography variant="body2" color="error">
          {errors.form}
        </Typography>
      )}
      <ButtonContainer>
        <Button onClick={handleNext} variant="contained" color="secondary">
          Next
        </Button>
      </ButtonContainer>
    </FormContainer>
  )

  const renderSelectInsuranceStep = () => (
    <FormContainer>
      <Typography variant="body2" gutterBottom>
        We found multiple insurance records. Please select one to update or
        choose "Add New".
      </Typography>
      {multipleInsurances.map((insurance, index) => (
        <div key={index} style={{ margin: '8px 0' }}>
          <input
            type="radio"
            name="existingInsurance"
            checked={selectedInsuranceIndex === index}
            onChange={() => setSelectedInsuranceIndex(index)}
          />
          <label style={{ marginLeft: '6px' }}>
            {insurance.patient.insuranceCompany} – Member #
            {insurance.patient.memberId}
          </label>
        </div>
      ))}
      <div style={{ margin: '8px 0' }}>
        <input
          type="radio"
          name="existingInsurance"
          checked={selectedInsuranceIndex === -1}
          onChange={() => setSelectedInsuranceIndex(-1)}
        />
        <label style={{ marginLeft: '6px' }}>Add a new insurance</label>
      </div>
      {errors.form && (
        <Typography variant="body2" color="error">
          {errors.form}
        </Typography>
      )}
      <ButtonContainer>
        <Button
          onClick={handleInsuranceSelectionNext}
          variant="contained"
          color="secondary"
        >
          Next
        </Button>
      </ButtonContainer>
    </FormContainer>
  )

  const renderPatientFields = () => (
    <>
      <Row>
        <TextField
          label="First Name *"
          name="firstName"
          placeholder="First Name"
          fullWidth
          value={formValues.firstName || ''}
          onChange={e => setFormValues({ ...formValues, firstName: e })}
          error={errors?.firstName}
        />
        <TextField
          label="Last Name *"
          name="lastName"
          placeholder="Last Name"
          fullWidth
          value={formValues.lastName || ''}
          onChange={e => setFormValues({ ...formValues, lastName: e })}
          error={errors?.lastName}
        />
        <MobileInputWrapper>
          <DateOfBirthInput
            label="Date of birth"
            required
            value={formValues.dateOfBirth || ''}
            error={errors?.dateOfBirth}
            onChange={e => setFormValues({ ...formValues, dateOfBirth: e })}
            helpText="Enter date as MM/DD/YYYY"
          />
        </MobileInputWrapper>
      </Row>
      <Row>
        <Typography variant="body2" gutterBottom>
          Patient Gender
        </Typography>
      </Row>
      <Row>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formValues.gender === 'male'}
              onChange={() =>
                setFormValues({
                  ...formValues,
                  gender: formValues.gender === 'male' ? null : 'male',
                })
              }
            />
            Male
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formValues.gender === 'female'}
              onChange={() =>
                setFormValues({
                  ...formValues,
                  gender: formValues.gender === 'female' ? null : 'female',
                })
              }
            />
            Female
          </CheckboxLabel>
        </CheckboxGroup>
      </Row>
      {errors.gender && <Typography color="error">{errors.gender}</Typography>}
      <Row>
        <TextField
          label="Employer Name"
          name="employerName"
          placeholder="Employer Name"
          fullWidth
          value={formValues.employerName || ''}
          onChange={e => setFormValues({ ...formValues, employerName: e })}
          error={errors?.employerName}
        />
      </Row>
      {isSigned && (
        <>
          <Row>
            <PayerSearch
              value={
                formValues.payerId
                  ? {
                      id: formValues.payerId,
                      code: formValues.payerCode,
                      name: formValues.insuranceCompany,
                    }
                  : null
              }
              onChange={value => {
                setFormValues({
                  ...formValues,
                  payerId: value?.id,
                  payerCode: value?.code,
                  insuranceCompany: value?.name,
                })
              }}
              error={errors?.payerCode}
            />
          </Row>
          <Row>
            <TextField
              label="Policy Holder/Member ID#"
              name="memberId"
              placeholder="Member ID"
              fullWidth
              value={formValues.memberId || ''}
              onChange={e => setFormValues({ ...formValues, memberId: e })}
              error={errors?.memberId}
            />
            <TextField
              label="Plan/Group Number"
              name="planNumber"
              placeholder="Plan/Group Number"
              fullWidth
              value={formValues.planNumber || ''}
              onChange={e => setFormValues({ ...formValues, planNumber: e })}
              error={errors?.planNumber}
            />
          </Row>
        </>
      )}
    </>
  )

  const renderSubscriberFields = () => (
    <>
      <Row>
        <Typography variant="body2" gutterBottom>
          Subscriber relationship to patient
        </Typography>
      </Row>
      <Row>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formValues.relationToPatient === 'self'}
              onChange={() =>
                setFormValues({
                  ...formValues,
                  relationToPatient:
                    formValues.relationToPatient === 'self' ? null : 'self',
                  policyHolderFirstName:
                    formValues.relationToPatient === 'self'
                      ? ''
                      : formValues.firstName,
                  policyHolderLastName:
                    formValues.relationToPatient === 'self'
                      ? ''
                      : formValues.lastName,
                  policyHolderDateOfBirth:
                    formValues.relationToPatient === 'self'
                      ? ''
                      : formValues.dateOfBirth,
                  policyHolderGender:
                    formValues.relationToPatient === 'self'
                      ? ''
                      : formValues.gender,
                })
              }
            />
            Self
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formValues.relationToPatient === 'spouse'}
              onChange={() =>
                setFormValues({
                  ...formValues,
                  relationToPatient:
                    formValues.relationToPatient === 'spouse' ? null : 'spouse',
                  policyHolderFirstName: '',
                  policyHolderLastName: '',
                  policyHolderDateOfBirth: '',
                  policyHolderGender: '',
                })
              }
            />
            Spouse
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formValues.relationToPatient === 'child'}
              onChange={() =>
                setFormValues({
                  ...formValues,
                  relationToPatient:
                    formValues.relationToPatient === 'child' ? null : 'child',
                  policyHolderFirstName: '',
                  policyHolderLastName: '',
                  policyHolderDateOfBirth: '',
                  policyHolderGender: '',
                })
              }
            />
            Dependent Child
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formValues.relationToPatient === 'other'}
              onChange={() =>
                setFormValues({
                  ...formValues,
                  relationToPatient:
                    formValues.relationToPatient === 'other' ? null : 'other',
                  policyHolderFirstName: '',
                  policyHolderLastName: '',
                  policyHolderDateOfBirth: '',
                  policyHolderGender: '',
                })
              }
            />
            Other
          </CheckboxLabel>
        </CheckboxGroup>
      </Row>
      {errors.relationToPatient && (
        <Typography color="error">{errors.relationToPatient}</Typography>
      )}
      <Row>
        <TextField
          label="First Name"
          name="policyHolderFirstName"
          placeholder="First Name"
          fullWidth
          value={formValues.policyHolderFirstName || ''}
          onChange={e =>
            setFormValues({ ...formValues, policyHolderFirstName: e })
          }
          error={errors?.policyHolderFirstName}
        />
        <TextField
          label="Last Name"
          name="policyHolderLastName"
          placeholder="Last Name"
          fullWidth
          value={formValues.policyHolderLastName || ''}
          onChange={e =>
            setFormValues({ ...formValues, policyHolderLastName: e })
          }
          error={errors?.policyHolderLastName}
        />
        <MobileInputWrapper>
          <DateOfBirthInput
            label="Date of birth"
            value={formValues.policyHolderDateOfBirth || ''}
            error={errors?.policyHolderDateOfBirth}
            onChange={e =>
              setFormValues({ ...formValues, policyHolderDateOfBirth: e })
            }
            helpText="Enter date as MM/DD/YYYY"
          />
        </MobileInputWrapper>
      </Row>
      <Row>
        <Typography variant="body2" gutterBottom>
          Policy Holder Gender
        </Typography>
      </Row>
      <Row>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formValues.policyHolderGender === 'male'}
              onChange={() =>
                setFormValues({
                  ...formValues,
                  policyHolderGender:
                    formValues.policyHolderGender === 'male' ? null : 'male',
                })
              }
            />
            Male
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formValues.policyHolderGender === 'female'}
              onChange={() =>
                setFormValues({
                  ...formValues,
                  policyHolderGender:
                    formValues.policyHolderGender === 'female'
                      ? null
                      : 'female',
                })
              }
            />
            Female
          </CheckboxLabel>
        </CheckboxGroup>
      </Row>
      <Row>
        <TextField
          label="Policy Holder/Member ID#"
          name="policyHolderMemberId"
          placeholder="Member ID"
          fullWidth
          value={formValues.policyHolderMemberId || ''}
          onChange={e =>
            setFormValues({ ...formValues, policyHolderMemberId: e })
          }
          error={errors?.policyHolderMemberId}
        />
      </Row>
      {errors.policyHolderGender && (
        <Typography color="error">{errors.policyHolderGender}</Typography>
      )}
    </>
  )

  const handleChangeStatus = e => {
    setFormValues({ ...formValues, status: e })
    setStatusChanged(true)
  }

  const renderPatientAndSubscriberForm = () => (
    <PageContainer>
      {!isSigned && (
        <InfoBox>
          <Typography variant="body2" gutterBottom>
            If your insurance covers orthodontic treatment, we will be happy to
            submit a claim on your behalf for reimbursement. Simply complete
            this form and we'll take care of the rest. Questions? Call us at
            617-449-3608 or email{' '}
            <a href="mailto:hello@smilebar.com">hello@smilebar.com</a>
          </Typography>
        </InfoBox>
      )}

      <Typography variant="h2" gutterBottom>
        Patient Information
      </Typography>
      {!isSigned && (
        <InfoBox>
          <Typography variant="body2" gutterBottom>
            Most of the information needed can be found on your insurance card.
            We recommend calling your insurance company if you need assistance
            completing any of the below fields.
          </Typography>
        </InfoBox>
      )}

      <StepCard>
        <TitleContainer>
          <StepIndicator>3</StepIndicator>
          <Typography variant="h3" gutterBottom mt={1}>
            Enter the patient's information
          </Typography>
        </TitleContainer>
        {renderPatientFields()}
      </StepCard>

      <Typography variant="h2" gutterBottom>
        Policy Holder (Optional)
      </Typography>
      {!isSigned && (
        <InfoBox>
          <Typography variant="body2" gutterBottom>
            If you are a dependent, please complete the policy holder's
            information.
          </Typography>
        </InfoBox>
      )}

      <StepCard>
        <TitleContainer>
          <StepIndicator>4</StepIndicator>
          <Typography variant="h3" gutterBottom mt={1}>
            Enter the subscriber's information
          </Typography>
        </TitleContainer>
        {renderSubscriberFields()}
      </StepCard>

      <Typography variant="h2" gutterBottom>
        Insurance ID Card
      </Typography>
      {!isSigned && (
        <InfoBox>
          <Typography variant="body2" gutterBottom>
            Please upload an image of the front and back of your insurance card.
          </Typography>
        </InfoBox>
      )}

      <StepCard>
        <InsuranceCardUploads
          frontUrl={formValues.insuranceCardFrontUrl}
          backUrl={formValues.insuranceCardBackUrl}
          errors={errors}
          onUpload={({ front, back }) =>
            setFormValues(prev => ({
              ...prev,
              insurance_card_front: front,
              insurance_card_back: back,
            }))
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body1" sx={{ mx: 2 }}>
            Or
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>
        <TextField
          fullWidth
          label="Policy Holder/Subscriber ID (SSN or ID#)"
          name="staff_consult_policy_holder_subscriber_id"
          value={formValues.staff_consult_policy_holder_subscriber_id || ''}
          onChange={e =>
            setFormValues({
              ...formValues,
              staff_consult_policy_holder_subscriber_id: e,
            })
          }
        />
      </StepCard>
      {isSigned && (
        <>
          {' '}
          <Typography variant="h2" gutterBottom>
            Insurance Status
          </Typography>
          <InfoBox>
            <Typography variant="body2" gutterBottom>
              <Typography variant="body2" gutterBottom>
                After you have properly filled out all fields, please change the
                status to "Processed" to finalize the submission and be able to
                run an Eligibility.
              </Typography>
            </Typography>
          </InfoBox>
          <StepCard>
            <SelectField
              fullWidth
              noMargin
              name="status"
              placeholder="Choose an option"
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'processed', label: 'Validated' },
              ]}
              value={formValues.status}
              onChange={handleChangeStatus}
            />
            {formValues.status === 'processed' && (
              <WarningBox>
                <Typography variant="body2" gutterBottom>
                  Once you mark the insurance form as processed, it will
                  automatically mark the task as completed.
                </Typography>
              </WarningBox>
            )}

            {statusChanged &&
              formValues.status === 'draft' &&
              previousStatus.current === 'processed' && (
                <WarningBox>
                  <Typography variant="body2" gutterBottom>
                    If you change the status back to draft, a new task will be
                    created.
                  </Typography>
                </WarningBox>
              )}
          </StepCard>
        </>
      )}

      <ButtonContainer>
        <Button onClick={handleSubmit} variant="contained" color="secondary">
          {method === 'put' ? 'Update Insurance' : 'Submit Insurance'}
        </Button>
      </ButtonContainer>
    </PageContainer>
  )

  const renderSuccessStep = () => (
    <FormContainer>
      <Typography variant="h5" gutterBottom>
        🎉 Success!
      </Typography>
      <Typography variant="body1">
        Your insurance details have been submitted successfully.
      </Typography>
      {isSigned && (
        <Button
          onClick={() => {
            navigate(-1)
            // clear the query cache
            queryClient.clear()
            // reload patient query
            queryClient.refetchQueries({
              queryKey: ['patient', patientHashId],
            })
          }}
          variant="contained"
          color="secondary"
        >
          Go back to Patient Profile
        </Button>
      )}
    </FormContainer>
  )

  const stepper = {
    1: renderVerificationStep(),
    2: renderSelectInsuranceStep(),
    3: renderPatientAndSubscriberForm(),
    4: renderSuccessStep(),
  }

  const stepperTitle = {
    1: 'Verify Your Identity',
    2: 'Select Insurance to Update or Add New',
    3: 'Patient & Subscriber Information',
    4: 'Success!',
  }

  return (
    <FormScrollWrapper ref={formRef}>
      <ScrollableWrapper theme={theme}>
        {step === 1 || step === 2 || step === 4 ? (
          <FullScreenCard>
            <TitleContainer>
              <StepIndicator>{step}</StepIndicator>
              <Typography variant="h2" gutterBottom mt={1}>
                {stepperTitle[step]}
              </Typography>
            </TitleContainer>
            {stepper[step]}
          </FullScreenCard>
        ) : (
          <>{stepper[step]}</>
        )}
      </ScrollableWrapper>
    </FormScrollWrapper>
  )
}

export default InsuranceForm
